import type { CloudEnvironment } from '../types/CloudEnvironment';
import type {
    CloudEnvironmentInfo,
    InputUrlType,
    NewAccountCloudEnvironmentInfo,
} from '../types/CloudEnvironmentInfo';
import { ensureOriginInRequest, ensureOriginInString } from 'owa-url/lib/ensureOrigin';
import { isSovereignEnvironment } from './isSovereignEnvironment';

export class CloudEnvironmentInfoImpl implements CloudEnvironmentInfo {
    protected environment: CloudEnvironment;

    constructor(environment: CloudEnvironment) {
        this.environment = environment;
    }

    buildOutlookUrl<T extends string | Request>(originalUrl: T): InputUrlType<T> {
        if (typeof originalUrl === 'string') {
            return ensureOriginInString(
                originalUrl,
                this.environment.outlookWebOrigin
            ) as InputUrlType<T>;
        }

        return ensureOriginInRequest(
            originalUrl,
            this.environment.outlookWebOrigin
        ) as InputUrlType<T>;
    }

    msaAuthenticationEndpoint(): string | undefined {
        return this.environment.msaAuthenticationEndpoint;
    }

    orgAuthenticationEndpoint(): string {
        return this.environment.orgAuthenticationEndpoint;
    }

    telemetrySafeDiagnosticData(): string {
        return JSON.stringify({
            envKey: this.environment.environmentKey,
            msaAuthEndpoint: this.environment.msaAuthenticationEndpoint,
            orgAuthEndpoint: this.environment.orgAuthenticationEndpoint,
            outlookWebOrigin: this.environment.outlookWebOrigin,
            usesGlobalDirectory: this.environment.usesGlobalDirectory,
        });
    }

    tenantId(): string | undefined {
        return this.environment.tenantId;
    }

    // Protected impl method to access the implementation details for cross-instance comparisons
    protected allowUsageAlongsideAccountsInImpl(other: CloudEnvironmentInfoImpl): boolean {
        // We allow accounts from environments to coexist if they have the same Outlook Web origin
        return this.environment.outlookWebOrigin === other.environment.outlookWebOrigin;
    }
}

export class NewAccountCloudEnvironmentInfoImpl
    extends CloudEnvironmentInfoImpl
    implements NewAccountCloudEnvironmentInfo
{
    allowUsageAlongsideAccountsIn(other: CloudEnvironmentInfo): boolean {
        if (!(other instanceof CloudEnvironmentInfoImpl)) {
            return false;
        }

        return super.allowUsageAlongsideAccountsInImpl(other);
    }

    isSovereign(): boolean {
        return isSovereignEnvironment(this.environment);
    }
}
